import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

/**
 * Sticky save footer for the pedigree sidebar.
 *
 * Props:
 *   dirtyCount        — number of dirty sections
 *   dirtySectionLabels — human-readable names of dirty sections
 *   onSave            — async () => void — called when "Save changes" clicked
 *   onDiscard         — () => void — called when discard is confirmed
 */
export default function SaveFooter({
  dirtyCount,
  dirtySectionLabels,
  onSave,
  onDiscard
}) {
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  // Navigation guard — warn on page unload when there are unsaved changes
  useEffect(() => {
    if (dirtyCount === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyCount]);

  const handleSave = async () => {
    if (saving || dirtyCount === 0) return;
    setSaving(true);
    try {
      await onSave();
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardConfirm = () => {
    setDiscardOpen(false);
    onDiscard();
  };

  const visible = dirtyCount > 0 || saving;

  return (
    <>
      <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <Button
            variant="outlined"
            size="medium"
            color="inherit"
            disabled={saving}
            onClick={() => setDiscardOpen(true)}
          >
            Discard
          </Button>

          <Button
            variant="contained"
            size="medium"
            color="primary"
            disabled={saving}
            onClick={handleSave}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
          >
            Save
          </Button>

          <Typography
            variant="body2"
            sx={{
              ml: 0.5,
              color: 'warning.dark',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {dirtyCount} unsaved section{dirtyCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Slide>
      {/* Discard confirmation dialog */}
      <Dialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        maxWidth="xs"
        fullWidth
        role="alertdialog"
        aria-label="Confirm discard"
      >
        <DialogTitle sx={{ pb: 1 }}>Discard unsaved changes?</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary'
            }}
          >
            {dirtySectionLabels.length > 0 ? (
              <>
                Unsaved changes to{' '}
                <strong>{dirtySectionLabels.join(' and ')}</strong> will be
                lost.
              </>
            ) : (
              'All unsaved changes will be lost.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            autoFocus
            onClick={() => setDiscardOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={handleDiscardConfirm}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
